import React, { Fragment, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useField } from 'formik';

import { buildDimensionReportTree } from './buildDimensionReportTree';
import { useBudgetByDimensionReportTableContext } from './context/BudgetByDimensionReportTableContext';

import { formatFigure } from 'Helpers/formatService';

import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import EditIcon from 'Assets/icons/edit.svg';
import PlusIcon from 'Assets/icons/plus.svg';

/**
 * On-screen table for Budgets by Venue / Budgets by Event.
 *
 * The backend service returns a flat array of rows in three sheets:
 *   summary, detailed, detailed_comparison
 *
 * report_type is read from criteria to select which sheet to render:
 *
 * summary (default):
 *   rows = department/group labels
 *   columns = one per dimension (venue/sport) + Total
 *   row shape: { department, [dimId]: number, ..., total }
 *
 * detailed:
 *   one row per budget line × dimension, with a single value column
 *   row shape: { type, venue, department, costcenter, project, template_pack, item, total }
 *
 * detailed_comparison ("Side-by-side Comparison"):
 *   one row per budget line with one value column per dimension + Total
 *   row shape: { type, department, costcenter, project, template_pack, item, [dimId]: number, ..., total }
 *
 * Detailed and side-by-side rows carry `_node` tree metadata, which
 * buildDimensionReportTree turns into the first-column hierarchy (Type →
 * Workforce/Non-Workforce → Department → Cost Centre → Project → Template/Pack
 * → Item) with per-node expand/collapse — the same layout as the Budgets by
 * Department report. Snapshots stored before that metadata existed fall back
 * to the legacy flat renderers, which spread the hierarchy across columns and
 * collapse per section header (groups style) using the styles object's
 * 1-based row indices (totals / group_totals / groups / sub_totals /
 * group_sub_totals).
 */

const REPORT_TYPE_SUMMARY = 'summary';
const REPORT_TYPE_DETAILED = 'detailed';
const REPORT_TYPE_COMPARISON = 'detailed_comparison';

// ─── Row styling helpers ───────────────────────────────────────────────────

/**
 * Build a Set of style indices from the styles object.
 * Styles are 1-based row numbers.
 */
const buildStyleSets = (styles = {}) => ({
    totalRowIndices: new Set([...(styles.totals ?? []), ...(styles.sub_totals ?? [])]),
    groupRowIndices: new Set(styles.groups ?? []),
    groupTotalRowIndices: new Set([...(styles.group_totals ?? []), ...(styles.group_sub_totals ?? [])]),
});

/**
 * Classify a row given its index (1-based) and label.
 * Returns { isGroup, isTotal, isGroupTotal, isNetRow, isTotalRow, isSubGroupTotal }
 */
const classifyRow = (rowNumber, label, { totalRowIndices, groupRowIndices, groupTotalRowIndices }) => {
    const isGroup = groupRowIndices.has(rowNumber);
    const isTotal = totalRowIndices.has(rowNumber);
    const isGroupTotal = groupTotalRowIndices.has(rowNumber);
    const isNetRow = label === $t('Net');
    const isTotalRow = isTotal && !isNetRow;
    const isSubGroupTotal = isGroupTotal && !isNetRow && !isTotalRow;
    return { isGroup, isTotal, isGroupTotal, isNetRow, isTotalRow, isSubGroupTotal };
};

const rowClassNames = ({ isTotalRow, isNetRow, isSubGroupTotal }) =>
    classNames('tw-text-xs', {
        '!tw-bg-blue !tw-text-white': isTotalRow,
        '!tw-bg-charcoal !tw-text-white': isNetRow,
        '!tw-bg-charcoal-10': isSubGroupTotal,
        '!tw-bg-white': !isTotalRow && !isNetRow && !isSubGroupTotal,
    });

const labelCellClassNames = ({ isTotalRow, isNetRow, isSubGroupTotal }) =>
    classNames({
        '!tw-bg-charcoal-15 tw-text-charcoal': !isTotalRow && !isNetRow && !isSubGroupTotal,
    });

const valueCellClassNames = ({ isTotalRow, isNetRow, isSubGroupTotal, last = false }) =>
    classNames('inbud-numeric-cell', {
        '!tw-bg-[#D8D8D8]': !isTotalRow && !isNetRow && !isSubGroupTotal,
        '!tw-border-r-0': last,
    });

const renderMoney = (val) => formatFigure(val);

// ─── Online-review comment column ─────────────────────────────────────────

/**
 * Trailing header for the comment column, only rendered during online review.
 */
const CommentsHeader = ({ showComments }) => {
    if (!showComments) return null;
    return <th className="tw-min-w-56 !tw-border-r-0">{$t('Comments')}</th>;
};

/**
 * A comment cell for a single data row, keyed by a stable budget_line_key
 * (`<sheet>:<idx>`). Shows prior comments read-only and, when editable, a
 * Formik-bound input for the reviewer's own comment.
 */
const CommentCell = ({ budgetLineKey }) => {
    const { comments, allComments, editableComments } = useBudgetByDimensionReportTableContext();

    const lineComments = useMemo(() => allComments?.[budgetLineKey] || [], [allComments, budgetLineKey]);
    const editableValue = useMemo(() => comments?.[budgetLineKey] ?? '', [comments, budgetLineKey]);
    const [editable, setEditable] = useState(false);

    const commentList = lineComments.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-0.5">
            {lineComments.map((c) => (
                <span key={c.id} className={c.author_type === 'reviewer' ? 'tw-text-charcoal' : 'tw-text-red'}>
                    {c.author_name} ({c.author_type === 'reviewer' ? $t('Reviewer') : $t('User')}): {c.comment}
                </span>
            ))}
        </div>
    );

    if (!editableComments) {
        return commentList || null;
    }

    if (editable) {
        return (
            <>
                {commentList}
                <CommentInput budgetLineKey={budgetLineKey} setEditable={setEditable} />
            </>
        );
    }

    return (
        <div onClick={() => setEditable(true)} className="tw-w-full tw-cursor-pointer tw-opacity-80 hover:tw-opacity-100">
            {commentList}
            <div className="tw-flex tw-items-center tw-gap-1 hover:tw-underline">
                {editableValue ? (
                    <>
                        <span className="tw-text-charcoal-50 tw-italic">{editableValue}</span>
                        <EditIcon className="tw-w-2.5 tw-h-2.5" />
                    </>
                ) : (
                    <>
                        <span className="tw-text-charcoal-50">{$t('Add comment')}</span>
                        <PlusIcon className="tw-w-2.5 tw-h-2.5" />
                    </>
                )}
            </div>
        </div>
    );
};

const CommentInput = ({ budgetLineKey, setEditable }) => {
    const [field] = useField({ name: `comments.${budgetLineKey}` });

    return (
        <input
            {...field}
            value={field.value ?? ''}
            type="text"
            className="tw-w-full tw-h-auto tw-px-1.5 tw-py-0.5 tw-text-xs tw-border tw-border-blue tw-rounded tw-outline-none"
            placeholder={$t('Enter comment...')}
            autoFocus
            onBlur={() => setEditable(false)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'Escape') {
                    setEditable(false);
                }
            }}
        />
    );
};

/**
 * Comment cell for a data row, or an empty spacer cell for non-data rows
 * (section headers / totals / nets). Returns null when comments are hidden.
 */
const CommentTd = ({ showComments, isDataRow, budgetLineKey, ...rest }) => {
    if (!showComments) return null;

    if (!isDataRow) {
        return <td className="!tw-border-r-0" {...rest} />;
    }

    return (
        <td className="tw-relative !tw-border-r-0" onClick={(e) => e.stopPropagation()} {...rest}>
            <CommentCell budgetLineKey={budgetLineKey} />
        </td>
    );
};

// ─── Section-header grouping for expand / collapse ────────────────────────

/**
 * Given a flat row array with style sets, compute which rows belong to
 * which section header (the nearest preceding `groups`-styled row).
 * sectionKey is the department label of the section header (e.g. "Revenue", "Expenditure").
 * Returns an array of { row, idx, rowNumber, sectionKey } objects.
 */
const annotateRowsWithSection = (rows, groupRowIndices, getSectionLabel) => {
    let currentSection = null;
    return rows.map((row, idx) => {
        const rowNumber = idx + 1;
        // Array rows are empty spacers; they cannot be section headers
        if (!Array.isArray(row) && groupRowIndices.has(rowNumber)) {
            currentSection = getSectionLabel(row);
        }
        return { row, idx, rowNumber, sectionKey: currentSection };
    });
};

// ─── Summary table ────────────────────────────────────────────────────────

const SummaryTable = ({ rows, styles, dimensions, loading }) => {
    const { showComments } = useBudgetByDimensionReportTableContext();
    const columnCount = 1 + (dimensions ? dimensions.length : 0) + 1 + (showComments ? 1 : 0);
    const styleSets = useMemo(() => buildStyleSets(styles), [styles]);

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/3 tw-min-w-56">{$t('Department / Category')}</th>
                    {dimensions &&
                        dimensions.map((dim) => (
                            <th key={dim.id} className="tw-min-w-36 tw-max-w-48">
                                {dim.name}
                            </th>
                        ))}
                    <th className={classNames('tw-min-w-36', { '!tw-border-r-0': !showComments })}>{$t('Total')}</th>
                    <CommentsHeader showComments={showComments} />
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && !rows.length && (
                    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                        <td colSpan={columnCount} className="tw-p-4">
                            <span>{$t('No data available')}</span>
                        </td>
                    </tr>
                )}
                {!loading &&
                    rows.length > 0 &&
                    rows.map((row, idx) => {
                        // pushEmptyRow produces an indexed PHP array → JS array; skip those
                        if (Array.isArray(row)) return null;

                        const rowNumber = idx + 1;
                        const dept = row.department;
                        if (!dept || dept === '') return null;

                        const classification = classifyRow(rowNumber, dept, styleSets);
                        const { isGroup } = classification;

                        if (isGroup) {
                            return (
                                <tr key={idx} className="tw-font-medium !tw-bg-charcoal-75">
                                    <td className="!tw-border-r-0 tw-text-white">{dept}</td>
                                    <td colSpan={columnCount - 1}></td>
                                </tr>
                            );
                        }

                        const isDataRow = !classification.isTotalRow && !classification.isNetRow && !classification.isSubGroupTotal;

                        return (
                            <tr key={idx} className={rowClassNames(classification)}>
                                <td className={labelCellClassNames(classification)}>{dept}</td>
                                {dimensions &&
                                    dimensions.map((dim) => (
                                        <td key={dim.id} className={valueCellClassNames(classification)}>
                                            {renderMoney(row[dim.id])}
                                        </td>
                                    ))}
                                <td className={valueCellClassNames({ ...classification, last: !showComments })}>{renderMoney(row.total)}</td>
                                <CommentTd showComments={showComments} isDataRow={isDataRow} budgetLineKey={`summary:${idx}`} />
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    );
};

// ─── Tree rendering (first-column hierarchy) ──────────────────────────────

/**
 * One expandable hierarchy row plus — when expanded — its leaf lines and child
 * nodes, rendered recursively. Mirrors DepartmentsBudgetsReportTableRow: the
 * label sits in the sticky first column with chevron + indentation, and the
 * node's subtotals render in the value columns.
 *
 * renderValueCells(values, { leafRow }) returns the sheet-specific value cells
 * (Venue/Cash/Total for detailed, one column per dimension + Total for
 * side-by-side). `values` holds the row's numeric fields; `leafRow` is the
 * original row for leaf lines, null for node / total rows.
 */
const TreeNodeRows = ({ node, sheet, sheetKey, renderValueCells }) => {
    const { expanded, toggleNodeExpansion, showComments } = useBudgetByDimensionReportTableContext();

    const nodeKey = `tree:${sheet}:${node.key}`;
    const expandable = node.entries.length > 0;
    const isExpanded = expandable && !!expanded[nodeKey];

    return (
        <>
            <tr
                className={classNames('tw-text-xs !tw-bg-white', { 'tw-cursor-pointer': expandable })}
                onClick={() => expandable && toggleNodeExpansion(nodeKey)}
            >
                <td
                    className={classNames({
                        '!tw-bg-charcoal-15 tw-text-charcoal': node.level === 0,
                        '!tw-bg-charcoal-10 tw-text-charcoal': node.level === 1,
                    })}
                >
                    <div className="tw-flex tw-items-center tw-gap-x-1.5" style={{ paddingLeft: `${node.level * 0.75}rem` }}>
                        <div className="tw-w-1.5">
                            {expandable &&
                                (isExpanded ? (
                                    <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                ) : (
                                    <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal" />
                                ))}
                        </div>
                        <div>{node.label}</div>
                    </div>
                </td>
                {renderValueCells(node.values, { leafRow: null })}
                <CommentTd showComments={showComments} isDataRow={false} />
            </tr>
            {isExpanded &&
                node.entries.map((entry) =>
                    entry.type === 'leaf' ? (
                        <tr key={`leaf-${entry.idx}`} className="tw-text-xs !tw-bg-white">
                            <td>
                                <div style={{ paddingLeft: `${(node.level + 1) * 0.75}rem` }}>{entry.label}</div>
                            </td>
                            {renderValueCells(entry.row, { leafRow: entry.row })}
                            <CommentTd showComments={showComments} isDataRow budgetLineKey={`${sheetKey}:${entry.idx}`} />
                        </tr>
                    ) : (
                        <TreeNodeRows key={entry.node.key} node={entry.node} sheet={sheet} sheetKey={sheetKey} renderValueCells={renderValueCells} />
                    ),
                )}
        </>
    );
};

/**
 * The tbody rows for a tree-mode sheet: charcoal section headers, the
 * recursive hierarchy, and the always-visible total rows (Total Revenue /
 * Total Expenditure styled blue, Net styled charcoal).
 */
const TreeBlocks = ({ blocks, sheet, sheetKey, columnCount, renderValueCells }) => {
    const { showComments } = useBudgetByDimensionReportTableContext();

    return (
        <>
            {blocks.map((block, blockIdx) => {
                if (block.kind === 'section') {
                    return (
                        <tr key={`section-${blockIdx}`} className="tw-font-medium !tw-bg-charcoal-75">
                            <td className="!tw-border-r-0 tw-text-white">{block.label}</td>
                            <td colSpan={columnCount - 1}></td>
                        </tr>
                    );
                }

                if (block.kind === 'total') {
                    const isNetRow = block.label === $t('Net');

                    return (
                        <tr
                            key={`total-${blockIdx}`}
                            className={classNames('tw-text-xs', {
                                '!tw-bg-charcoal !tw-text-white': isNetRow,
                                '!tw-bg-blue !tw-text-white': !isNetRow,
                            })}
                        >
                            <td>{block.label}</td>
                            {renderValueCells(block.row, { leafRow: null })}
                            <CommentTd showComments={showComments} isDataRow={false} />
                        </tr>
                    );
                }

                return (
                    <Fragment key={`nodes-${blockIdx}`}>
                        {block.nodes.map((node) => (
                            <TreeNodeRows key={node.key} node={node} sheet={sheet} sheetKey={sheetKey} renderValueCells={renderValueCells} />
                        ))}
                    </Fragment>
                );
            })}
        </>
    );
};

/**
 * Detailed sheet, tree mode: hierarchy in the first column, then
 * Venue/Event | [Cash] | Total.
 */
const DetailedTreeTable = ({ tree, loading }) => {
    const { dimension, showComments, isOptionIncluded } = useBudgetByDimensionReportTableContext();
    // Venue reports use row.venue; event reports use row.sport
    const locationField = dimension === 'event' ? 'sport' : 'venue';
    const locationLabel = dimension === 'event' ? $t('Event') : $t('Venue');
    // Cash option (VIK split): a Cash column appears before Total
    const showCash = isOptionIncluded('cash');

    const columnCount = 3 + (showCash ? 1 : 0) + (showComments ? 1 : 0);

    const renderValueCells = (values, { leafRow }) => (
        <>
            <td>{leafRow ? leafRow[locationField] : ''}</td>
            {showCash && <td className="inbud-numeric-cell">{renderMoney(values.cash)}</td>}
            <td className={classNames('inbud-numeric-cell', { '!tw-bg-[#D8D8D8]': !!leafRow, '!tw-border-r-0': !showComments })}>
                {renderMoney(values.total)}
            </td>
        </>
    );

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/2 tw-min-w-72">{$t('Department / Cost Centre')}</th>
                    <th className="tw-min-w-40">{locationLabel}</th>
                    {showCash && <th className="tw-min-w-36">{$t('Cash')}</th>}
                    <th className={classNames('tw-min-w-36', { '!tw-border-r-0': !showComments })}>{$t('Total')}</th>
                    <CommentsHeader showComments={showComments} />
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && (
                    <TreeBlocks blocks={tree.blocks} sheet="detailed" sheetKey="detailed" columnCount={columnCount} renderValueCells={renderValueCells} />
                )}
            </tbody>
        </table>
    );
};

/**
 * Side-by-side comparison sheet, tree mode: hierarchy in the first column,
 * then one value column per dimension + Total.
 */
const DetailedComparisonTreeTable = ({ tree, dimensions, loading }) => {
    const { showComments } = useBudgetByDimensionReportTableContext();

    const columnCount = 1 + (dimensions ? dimensions.length : 0) + 1 + (showComments ? 1 : 0);

    const renderValueCells = (values, { leafRow }) => (
        <>
            {dimensions &&
                dimensions.map((dim) => (
                    <td key={dim.id} className="inbud-numeric-cell">
                        {renderMoney(values[dim.id])}
                    </td>
                ))}
            <td className={classNames('inbud-numeric-cell', { '!tw-bg-[#D8D8D8]': !!leafRow, '!tw-border-r-0': !showComments })}>
                {renderMoney(values.total)}
            </td>
        </>
    );

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/3 tw-min-w-72">{$t('Department / Cost Centre')}</th>
                    {dimensions &&
                        dimensions.map((dim) => (
                            <th key={dim.id} className="tw-min-w-36 tw-max-w-48">
                                {dim.name}
                            </th>
                        ))}
                    <th className={classNames('tw-min-w-36', { '!tw-border-r-0': !showComments })}>{$t('Total')}</th>
                    <CommentsHeader showComments={showComments} />
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && (
                    <TreeBlocks
                        blocks={tree.blocks}
                        sheet="comparison"
                        sheetKey="detailed_comparison"
                        columnCount={columnCount}
                        renderValueCells={renderValueCells}
                    />
                )}
            </tbody>
        </table>
    );
};

// ─── Detailed table ───────────────────────────────────────────────────────

/**
 * Detailed sheet dispatcher: tree mode when the rows carry `_node` metadata,
 * legacy flat columns otherwise (pre-metadata snapshots).
 */
const DetailedTable = ({ rows, styles, loading }) => {
    const tree = useMemo(() => buildDimensionReportTree(rows), [rows]);

    if (tree) {
        return <DetailedTreeTable tree={tree} loading={loading} />;
    }

    return <LegacyDetailedTable rows={rows} styles={styles} loading={loading} />;
};

/**
 * Detailed sheet, legacy flat mode: each row is a budget line with fields:
 *   type | venue | department | costcenter | project | template_pack | item | total
 *
 * Section headers (groups style) have: department set, venue/costcenter/project/item empty.
 * Group totals (group_totals style) show department subtotals.
 * Totals (totals style) show section totals (Total Revenue / Total Expenditure / Net).
 *
 * Expand / collapse: clicking a section header shows/hides the non-subtotal rows within it.
 */
const LegacyDetailedTable = ({ rows, styles, loading }) => {
    const { expanded, toggleExpansion, dimension, showComments, isOptionIncluded } = useBudgetByDimensionReportTableContext();
    // Venue reports use row.venue; event reports use row.sport
    const locationField = dimension === 'event' ? 'sport' : 'venue';
    const locationLabel = dimension === 'event' ? $t('Event') : $t('Venue');
    // Cash option (VIK split): a Cash column appears before Total
    const showCash = isOptionIncluded('cash');
    const styleSets = useMemo(() => buildStyleSets(styles), [styles]);
    const annotated = useMemo(() => annotateRowsWithSection(rows, styleSets.groupRowIndices, (row) => row.department || row.type), [rows, styleSets]);

    // A section is visible when expanded[`detailed:sectionLabel`] !== false
    const isSectionVisible = (sectionKey) => {
        if (sectionKey === null) return true;
        return expanded[`detailed:${sectionKey}`] !== false;
    };

    // Type | Venue | Dept | CC | Project | Template/Pack | Item | [Cash] | Total (+ Comments)
    const columnCount = 8 + (showCash ? 1 : 0) + (showComments ? 1 : 0);

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-min-w-32">{$t('Type')}</th>
                    <th className="tw-min-w-32">{locationLabel}</th>
                    <th className="tw-min-w-40">{$t('Department')}</th>
                    <th className="tw-min-w-40">{$t('Cost Centre')}</th>
                    <th className="tw-min-w-40">{$t('Project')}</th>
                    <th className="tw-min-w-40">{$t('Template/Pack')}</th>
                    <th className="tw-min-w-56">{$t('Item')}</th>
                    {showCash && <th className="tw-min-w-36">{$t('Cash')}</th>}
                    <th className={classNames('tw-min-w-36', { '!tw-border-r-0': !showComments })}>{$t('Total')}</th>
                    <CommentsHeader showComments={showComments} />
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && !rows.length && (
                    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                        <td colSpan={columnCount} className="tw-p-4">
                            <span>{$t('No data available')}</span>
                        </td>
                    </tr>
                )}
                {!loading &&
                    rows.length > 0 &&
                    annotated.map(({ row, idx, rowNumber, sectionKey }) => {
                        // pushEmptyRow produces an indexed PHP array → JS array; skip those
                        if (Array.isArray(row)) return null;

                        const classification = classifyRow(rowNumber, row.department, styleSets);
                        const { isGroup, isTotalRow, isNetRow, isSubGroupTotal } = classification;

                        // Section header row — clicking toggles child row visibility
                        if (isGroup) {
                            const sectionLabel = row.department || row.type;
                            return (
                                <tr
                                    key={idx}
                                    className="tw-font-medium !tw-bg-charcoal-75 tw-cursor-pointer"
                                    onClick={() => toggleExpansion(`detailed:${sectionLabel}`)}
                                >
                                    <td className="tw-text-white" colSpan={columnCount}>
                                        {sectionLabel}
                                    </td>
                                </tr>
                            );
                        }

                        // Hide individual data rows when section is collapsed
                        const isDataRow = !isTotalRow && !isNetRow && !isSubGroupTotal && !classification.isGroupTotal;
                        if (isDataRow && !isSectionVisible(sectionKey)) return null;

                        return (
                            <tr key={idx} className={rowClassNames(classification)}>
                                <td className={labelCellClassNames(classification)}>{row.type}</td>
                                <td className={labelCellClassNames(classification)}>{row[locationField]}</td>
                                <td className={labelCellClassNames(classification)}>{row.department}</td>
                                <td className={labelCellClassNames(classification)}>{row.costcenter}</td>
                                <td className={labelCellClassNames(classification)}>{row.project}</td>
                                <td className={labelCellClassNames(classification)}>{row.template_pack}</td>
                                <td className={labelCellClassNames(classification)}>{row.item}</td>
                                {showCash && <td className={valueCellClassNames(classification)}>{renderMoney(row.cash)}</td>}
                                <td className={valueCellClassNames({ ...classification, last: !showComments })}>{renderMoney(row.total)}</td>
                                <CommentTd showComments={showComments} isDataRow={isDataRow} budgetLineKey={`detailed:${idx}`} />
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    );
};

// ─── Detailed Comparison table ────────────────────────────────────────────

/**
 * Side-by-side comparison dispatcher: tree mode when the rows carry `_node`
 * metadata, legacy flat columns otherwise (pre-metadata snapshots).
 */
const DetailedComparisonTable = ({ rows, styles, dimensions, loading }) => {
    const tree = useMemo(() => buildDimensionReportTree(rows), [rows]);

    if (tree) {
        return <DetailedComparisonTreeTable tree={tree} dimensions={dimensions} loading={loading} />;
    }

    return <LegacyDetailedComparisonTable rows={rows} styles={styles} dimensions={dimensions} loading={loading} />;
};

/**
 * Detailed comparison sheet, legacy flat mode: each row has text label fields
 * plus one numeric column per dimension + total.
 * Row shape: { type, department, costcenter, project, template_pack, item, [dimId]: number, ..., total }
 *
 * Section headers (groups style) have: department set, costcenter/project/item empty.
 */
const LegacyDetailedComparisonTable = ({ rows, styles, dimensions, loading }) => {
    const { expanded, toggleExpansion, showComments } = useBudgetByDimensionReportTableContext();
    const styleSets = useMemo(() => buildStyleSets(styles), [styles]);
    const annotated = useMemo(() => annotateRowsWithSection(rows, styleSets.groupRowIndices, (row) => row.department || row.type), [rows, styleSets]);

    const isSectionVisible = (sectionKey) => {
        if (sectionKey === null) return true;
        return expanded[`comparison:${sectionKey}`] !== false;
    };

    // Label cols: Type | Dept | CC | Project | Template/Pack | Item  (6)
    // Value cols: one per dim + Total (+ Comments)
    const labelColCount = 6;
    const valueColCount = (dimensions ? dimensions.length : 0) + 1;
    const columnCount = labelColCount + valueColCount + (showComments ? 1 : 0);

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-min-w-32">{$t('Type')}</th>
                    <th className="tw-min-w-40">{$t('Department')}</th>
                    <th className="tw-min-w-40">{$t('Cost Centre')}</th>
                    <th className="tw-min-w-40">{$t('Project')}</th>
                    <th className="tw-min-w-40">{$t('Template/Pack')}</th>
                    <th className="tw-min-w-56">{$t('Item')}</th>
                    {dimensions &&
                        dimensions.map((dim) => (
                            <th key={dim.id} className="tw-min-w-36 tw-max-w-48">
                                {dim.name}
                            </th>
                        ))}
                    <th className={classNames('tw-min-w-36', { '!tw-border-r-0': !showComments })}>{$t('Total')}</th>
                    <CommentsHeader showComments={showComments} />
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && !rows.length && (
                    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                        <td colSpan={columnCount} className="tw-p-4">
                            <span>{$t('No data available')}</span>
                        </td>
                    </tr>
                )}
                {!loading &&
                    rows.length > 0 &&
                    annotated.map(({ row, idx, rowNumber, sectionKey }) => {
                        // pushEmptyRow produces an indexed PHP array → JS array; skip those
                        if (Array.isArray(row)) return null;

                        const classification = classifyRow(rowNumber, row.department, styleSets);
                        const { isGroup, isTotalRow, isNetRow, isSubGroupTotal } = classification;

                        // Section header row — clicking toggles child row visibility
                        if (isGroup) {
                            const sectionLabel = row.department || row.type;
                            return (
                                <tr
                                    key={idx}
                                    className="tw-font-medium !tw-bg-charcoal-75 tw-cursor-pointer"
                                    onClick={() => toggleExpansion(`comparison:${sectionLabel}`)}
                                >
                                    <td className="tw-text-white" colSpan={columnCount}>
                                        {sectionLabel}
                                    </td>
                                </tr>
                            );
                        }

                        // Hide individual data rows when section is collapsed
                        const isDataRow = !isTotalRow && !isNetRow && !isSubGroupTotal && !classification.isGroupTotal;
                        if (isDataRow && !isSectionVisible(sectionKey)) return null;

                        return (
                            <tr key={idx} className={rowClassNames(classification)}>
                                <td className={labelCellClassNames(classification)}>{row.type}</td>
                                <td className={labelCellClassNames(classification)}>{row.department}</td>
                                <td className={labelCellClassNames(classification)}>{row.costcenter}</td>
                                <td className={labelCellClassNames(classification)}>{row.project}</td>
                                <td className={labelCellClassNames(classification)}>{row.template_pack}</td>
                                <td className={labelCellClassNames(classification)}>{row.item}</td>
                                {dimensions &&
                                    dimensions.map((dim) => (
                                        <td key={dim.id} className={valueCellClassNames(classification)}>
                                            {renderMoney(row[dim.id])}
                                        </td>
                                    ))}
                                <td className={valueCellClassNames({ ...classification, last: !showComments })}>{renderMoney(row.total)}</td>
                                <CommentTd showComments={showComments} isDataRow={isDataRow} budgetLineKey={`detailed_comparison:${idx}`} />
                            </tr>
                        );
                    })}
            </tbody>
        </table>
    );
};

// ─── Main component ───────────────────────────────────────────────────────

const BudgetByDimensionReportTable = ({ actions }) => {
    const { report, dimensions, loading, criteria, toggleAllExpansion } = useBudgetByDimensionReportTableContext();

    const reportType = criteria?.report_type ?? REPORT_TYPE_SUMMARY;
    const styles = report?.styles ?? {};

    const renderTable = () => {
        if (reportType === REPORT_TYPE_DETAILED) {
            return <DetailedTable rows={report?.detailed ?? []} styles={styles.detailed ?? {}} loading={loading} />;
        }

        if (reportType === REPORT_TYPE_COMPARISON) {
            return (
                <DetailedComparisonTable
                    rows={report?.detailed_comparison ?? []}
                    styles={styles.detailed_comparison ?? {}}
                    dimensions={dimensions}
                    loading={loading}
                />
            );
        }

        // Default: summary
        return <SummaryTable rows={report?.summary ?? []} styles={styles.summary ?? {}} dimensions={dimensions} loading={loading} />;
    };

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-min-h-0 tw-gap-y-4">
            {/* Toolbar */}
            <div className="tw-flex tw-justify-between">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                    <div className="tw-flex tw-gap-x-1.5">
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion(true)}>
                            {$t('Expand all')}
                        </button>
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion(false)}>
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                {typeof actions === 'function' ? actions({ data: report }) : actions}
            </div>

            {/* Table */}
            <div className="tw-min-h-0">
                <div className="inbud-table_wrapper tw-h-full tw-overflow-auto">{renderTable()}</div>
            </div>
        </div>
    );
};

export default BudgetByDimensionReportTable;
